// =========================================================================
// HelpyDoc Metabolic · Auth Service (TypeScript)
// Dual-mode: localStorage mock (desarrollo) + Firebase Auth (producción)
//
// Modo mock:    siempre activo. Login/registro con localStorage.
// Modo Firebase: activo cuando VITE_FIREBASE_API_KEY está en .env
//               Instalar: npm install firebase en apps/web/
//               Ver: apps/web/.env.example
// =========================================================================

const LS_SESSION = 'helpydoc_session';
const LS_USERS   = 'helpydoc_users';

// ── Cuenta principal de la empresa, en desarrollo local ───────────────────
//
// En local NO hay Firebase —Vite no carga `.env.production*` en desarrollo—,
// así que se usa el mock, que guarda los usuarios en localStorage. Un
// navegador recién abierto no tiene ninguno, y cualquier intento de entrar
// devolvía «Usuario no encontrado» aunque la cuenta exista de verdad en
// producción. Eso es lo que hacía parecer que el acceso estaba roto.
//
// `info@helpydoc.com` es el correo OFICIAL de la empresa y el administrador
// principal del sistema. Existe de verdad en Firebase, con su rol asignado
// como custom claim. Lo que sigue NO le da ese rol ni suplanta esa cuenta:
// solo permite que en LOCAL, donde no hay Firebase, se pueda entrar sin tener
// que registrarse antes en cada navegador.
//
// No se guarda ninguna contraseña: se acepta cualquiera de 6+ caracteres, y
// la primera que se use queda fijada para esa sesión de trabajo. Guardar un
// hash aquí sería meter una credencial en el código.
//
// TRES GUARDAS, y las tres tienen que cumplirse:
//   1. `import.meta.env.DEV` — solo en desarrollo. En un build de producción
//      esto es false y la excepción NO existe.
//   2. Sin `VITE_FIREBASE_API_KEY` — si hay Firebase configurado, manda
//      Firebase y esto no se toca.
//   3. La lista es explícita. No es «cualquier email».
//
// Con las tres, un despliegue real nunca puede caer aquí: o tiene Firebase, o
// no es un build de desarrollo.
const CUENTAS_LOCALES = ['info@helpydoc.com'];

/** Lo que las guardas necesitan saber del entorno. */
export interface EntornoDeEjecucion {
  DEV?: unknown;
  VITE_FIREBASE_API_KEY?: unknown;
}

function _entornoReal(): EntornoDeEjecucion {
  return ((import.meta as { env?: EntornoDeEjecucion }).env ?? {}) as EntornoDeEjecucion;
}

/**
 * ¿Es este correo la cuenta principal de la empresa, en un entorno local que
 * cumple las tres guardas de arriba?
 *
 * Se exporta porque hay un segundo sitio que necesita la MISMA respuesta:
 * `lib/admin-rol.ts`, para decidir el rol en local. Duplicar allí las guardas
 * crearía una segunda fuente de verdad que puede desincronizarse — y la que se
 * quedara atrás sería la que abre el panel de más.
 *
 * `entorno` se inyecta para poder PROBAR las guardas. No es un adorno: sin él
 * la única forma de comprobar el caso «esto no debe existir en producción»
 * sería `vi.stubEnv`, que en esta configuración **no llega a
 * `import.meta.env`** — se escribió una prueba así y pasaba en verde sin
 * comprobar nada. Nadie en la aplicación pasa este segundo argumento.
 */
export function esCuentaPrincipalEnLocal(
  email: string,
  entorno: EntornoDeEjecucion = _entornoReal(),
): boolean {
  const enDesarrollo = Boolean(entorno.DEV);
  const hayFirebase = Boolean(entorno.VITE_FIREBASE_API_KEY);
  if (!enDesarrollo || hayFirebase) return false;
  return CUENTAS_LOCALES.includes(email.toLowerCase());
}

/**
 * La cuenta que hoy puede entrar sin registro previo, o `null` si esta
 * excepción no está activa.
 *
 * Existe para que la pantalla de acceso pueda ANUNCIARLA sin repetir ni el
 * correo ni las guardas. El cartel se escribió suelto y acabó viajando al
 * build de producción: allí no es solo inútil, es FALSO —esa cuenta no entra
 * sin contraseña real— y además publica la dirección de administración a
 * cualquiera que abra la pantalla de acceso. Atando texto y condición al mismo
 * sitio, no pueden volver a separarse.
 */
export function cuentaPrincipalDeLocal(
  entorno: EntornoDeEjecucion = _entornoReal(),
): string | null {
  const cuenta = CUENTAS_LOCALES[0];
  return cuenta && esCuentaPrincipalEnLocal(cuenta, entorno) ? cuenta : null;
}

// ── Firebase (cargado dinámicamente solo si está configurado) ─────────────
// La función _firebaseUser() retorna el usuario activo de Firebase Auth,
// o null si Firebase no está configurado/instalado.
type FirebaseUser = { getIdToken: (forceRefresh?: boolean) => Promise<string> };
let _fbUser: FirebaseUser | null = null;

/** Llamar tras un login/register con Firebase para registrar el usuario activo */
export function _setFirebaseUser(user: FirebaseUser | null): void {
  _fbUser = user;
}

// ── Interfaces ────────────────────────────────────────────────────────────

export interface Session {
  userId: string;
  email: string;
  nombre: string;
  /** Token de sesión. En modo mock: string aleatorio. En Firebase: UID (el token real se obtiene con getIdToken()) */
  token: string;
  isNew?: boolean;
  createdAt: string;
}

export interface AuthResult {
  ok: boolean;
  session?: Session;
  error?: string;
}

// ── Helpers internos ──────────────────────────────────────────────────────

function _getUsers(): Record<string, { passwordHash: string; nombre: string; email: string }> {
  try {
    const raw = localStorage.getItem(LS_USERS);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function _saveUsers(users: ReturnType<typeof _getUsers>): void {
  localStorage.setItem(LS_USERS, JSON.stringify(users));
}

function _hash(str: string): string {
  // Hash simple para modo mock (en producción: Firebase maneja las contraseñas)
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (Math.imul(31, h) + str.charCodeAt(i)) | 0;
  }
  return h.toString(36);
}

function _generateToken(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// ── AuthService ───────────────────────────────────────────────────────────

export const AuthService = {

  // ── Login (mock) ─────────────────────────────────────────────────────────
  login(email: string, password: string): AuthResult {
    const users = _getUsers();
    let user = users[email.toLowerCase()];

    // Cuenta de la empresa que todavía no existe en ESTE navegador: se crea al
    // vuelo. Ver CUENTAS_LOCALES arriba para las tres guardas que lo acotan.
    if (!user && esCuentaPrincipalEnLocal(email)) {
      if (password.length < 6) {
        return { ok: false, error: 'La contraseña debe tener al menos 6 caracteres' };
      }
      user = {
        passwordHash: _hash(password),
        nombre: 'HelpyDoc',
        email: email.toLowerCase(),
      };
      users[email.toLowerCase()] = user;
      _saveUsers(users);
    }

    if (!user) return { ok: false, error: 'Usuario no encontrado' };
    if (user.passwordHash !== _hash(password)) return { ok: false, error: 'Contraseña incorrecta' };

    const session: Session = {
      userId: email.toLowerCase(),
      email: email.toLowerCase(),
      nombre: user.nombre,
      token: _generateToken(),
      createdAt: new Date().toISOString(),
    };
    localStorage.setItem(LS_SESSION, JSON.stringify(session));
    return { ok: true, session };
  },

  // ── Registro (mock) ──────────────────────────────────────────────────────
  register(email: string, password: string, nombre: string): AuthResult {
    if (!email || !password || !nombre) return { ok: false, error: 'Todos los campos son obligatorios' };
    if (password.length < 6) return { ok: false, error: 'La contraseña debe tener al menos 6 caracteres' };

    const users = _getUsers();
    if (users[email.toLowerCase()]) return { ok: false, error: 'Este email ya está registrado' };

    users[email.toLowerCase()] = { passwordHash: _hash(password), nombre, email: email.toLowerCase() };
    _saveUsers(users);

    const session: Session = {
      userId: email.toLowerCase(),
      email: email.toLowerCase(),
      nombre,
      token: _generateToken(),
      isNew: true,
      createdAt: new Date().toISOString(),
    };
    localStorage.setItem(LS_SESSION, JSON.stringify(session));
    return { ok: true, session };
  },

  // ── Auto-login ───────────────────────────────────────────────────────────
  autoLogin(): AuthResult {
    try {
      const raw = localStorage.getItem(LS_SESSION);
      if (!raw) return { ok: false };
      const session = JSON.parse(raw) as Session;
      if (!session?.token) return { ok: false };
      return { ok: true, session };
    } catch { return { ok: false }; }
  },

  // ── Logout ───────────────────────────────────────────────────────────────
  logout(): void {
    _fbUser = null;
    localStorage.removeItem(LS_SESSION);
  },

  // ── Sesión actual ────────────────────────────────────────────────────────
  getSession(): Session | null {
    try {
      const raw = localStorage.getItem(LS_SESSION);
      return raw ? (JSON.parse(raw) as Session) : null;
    } catch { return null; }
  },

  isLoggedIn(): boolean {
    return !!AuthService.getSession();
  },

  // ── getIdToken ───────────────────────────────────────────────────────────
  /**
   * Retorna el Bearer token que debe usarse en cada petición al backend.
   *
   * - Modo mock (desarrollo):    devuelve session.token (string aleatorio, regenerado en cada login)
   * - Modo Firebase (producción): llama user.getIdToken(true) para obtener un ID Token
   *   válido y fresco (Firebase los renueva automáticamente cada hora).
   *
   * ApiClient llama este método en CADA request, no cachea el resultado,
   * garantizando que el token siempre esté vigente.
   *
   * Para activar modo Firebase:
   *   1. npm install firebase  (en apps/web/)
   *   2. Configurar VITE_FIREBASE_* en .env (ver .env.example)
   *   3. En firebase.ts inicializar Firebase y llamar _setFirebaseUser(user)
   *      tras cada login/register exitoso con Firebase Auth.
   */
  async getIdToken(): Promise<string | null> {
    // Modo Firebase: token real, siempre fresco
    if (_fbUser) {
      try {
        return await _fbUser.getIdToken(/* forceRefresh */ false);
      } catch {
        // Si falla la renovación, el usuario necesita re-autenticarse
        return null;
      }
    }

    // Modo mock: token de sesión guardado en localStorage
    return AuthService.getSession()?.token ?? null;
  },
};
